import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { User, Advisor } from '../types';
import { Spinner } from '../components/icons';

interface LoginPageProps {
  onLogin: (user: User | Advisor) => void;
  logoUrl?: string;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin, logoUrl }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const user = await api.login(email, password);
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      onLogin(user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ocurrió un error inesperado.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col justify-center items-center p-4">
      <div className="max-w-md w-full text-center">
        {logoUrl ? (
          <img src={logoUrl} alt="APEX University Logo" className="h-20 mx-auto mb-4" />
        ) : (
          <h1 className="text-4xl font-bold text-white">APEX University</h1>
        )}
        <p className="mt-2 text-xl text-gray-400">Curso Intensivo para Especialistas en Seguros</p>
      </div>
      <div className="max-w-sm w-full bg-slate-800 p-8 mt-10 rounded-xl shadow-lg animate-fade-in border border-slate-700">
        <h2 className="text-2xl font-bold text-center text-white mb-6">Iniciar Sesión</h2>
        <form onSubmit={handleLogin} className="space-y-6">
          <div className="space-y-4">
            <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 sr-only">Correo Electrónico</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  placeholder="Correo Electrónico"
                  className="w-full px-3 py-2 border border-slate-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-slate-700 text-white"
                />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 sr-only">Contraseña</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                placeholder="Contraseña"
                className="w-full px-3 py-2 border border-slate-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-slate-700 text-white"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
              <div className="flex items-center">
                  <input
                      id="remember-me"
                      name="remember-me"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.currentTarget.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-500 rounded bg-slate-700"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-400">
                      Recordar usuario
                  </label>
              </div>
          </div>
          
          {error && (
            <div className="bg-red-900 border border-red-700 text-red-200 p-3 rounded-md">
              <p className="text-sm">{error}</p>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-500 disabled:cursor-not-allowed"
            >
              {isLoading ? <Spinner className="h-5 w-5" /> : 'Ingresar'}
            </button>
          </div>
        </form>
      </div>
       <footer className="text-center text-gray-500 mt-8">
            <p>&copy; {new Date().getFullYear()} APEX One. Todos los derechos reservados.</p>
        </footer>
    </div>
  );
};

export default LoginPage;